import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {selectStyleGuide} from '../../data/session';
import {PAGE_NUMBER_ALIGN_ITEMS_MAP} from '../../constants';
import {
  FONT_LOAD_TIMEOUT_MS,
  FONT_APPLY_DELAY_MS,
} from './DocumentStyleProvider.constants';
import {applyDocumentStyleGuide} from './DocumentStyleProvider.utils';

export const DocumentStyleContext = createContext({fontsReady: true});

export function useDocumentStyle() {
  return useContext(DocumentStyleContext);
}

/**
 * Provides document-specific styling (fonts, margins, line height, etc.)
 * when the user is in the document workspace. Loads webfonts only when needed
 * and applies CSS variables from the org's document style guide.
 * Injected in WorkspaceLayout so it wraps all workspace routes (document, pdf-review, etc.).
 */
export function DocumentStyleProvider({children}) {
  const styleGuide = useSelector(selectStyleGuide);
  const documentPageNumberAlignment = useSelector(
    (state) =>
      state.documents.document.settings?.pagination?.pageNumberAlignment
  );
  const rootRef = useRef(document.documentElement);
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    let cancelled = false;
    let loadTimeoutId = null;
    let applyDelayId = null;

    try {
      const {fontToLoad, familyValue} = applyDocumentStyleGuide(
        root,
        styleGuide ?? null
      );

      if (fontToLoad && familyValue) {
        setFontsReady(false);
        const loadPromise = document.fonts.load(`1em "${fontToLoad}"`);
        const timeoutPromise = new Promise((resolve) => {
          loadTimeoutId = setTimeout(resolve, FONT_LOAD_TIMEOUT_MS);
        });
        Promise.race([loadPromise, timeoutPromise])
          .catch(() => {
            /* Ignore load/timeout failures — same outcome as success: apply family below */
          })
          .finally(() => {
            if (cancelled) return;
            root.style.setProperty('--document-font-family', familyValue);
            applyDelayId = setTimeout(() => {
              if (!cancelled) setFontsReady(true);
            }, FONT_APPLY_DELAY_MS);
          });
      } else {
        setFontsReady(true);
      }
    } catch (error) {
      console.error('Failed to apply document styles:', error);
      setFontsReady(true);
    }

    // Apply the document-level alignment on top of the org default.
    // Removing the property when unset lets the style guide value show through.
    if (documentPageNumberAlignment) {
      root.style.setProperty(
        '--page-number-alignment',
        documentPageNumberAlignment
      );
      root.style.setProperty(
        '--page-number-align-items',
        PAGE_NUMBER_ALIGN_ITEMS_MAP[documentPageNumberAlignment] ?? 'center'
      );
    } else {
      root.style.removeProperty('--page-number-alignment');
      root.style.removeProperty('--page-number-align-items');
    }

    return () => {
      cancelled = true;
      if (loadTimeoutId) clearTimeout(loadTimeoutId);
      if (applyDelayId) clearTimeout(applyDelayId);
    };
  }, [styleGuide, documentPageNumberAlignment]);

  return (
    <DocumentStyleContext.Provider value={{fontsReady}}>
      {children}
    </DocumentStyleContext.Provider>
  );
}

DocumentStyleProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DocumentStyleProvider;
